import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost, UnauthorizedError } from '../api';
import { Icon, Modal, fmtEpoch } from './ui';

// ไฟล์ที่ Sort-EDI-Files ส่งไป RETAILER-DATA/UNKNOW (หา receiver ใน cms_customers_*.csv ไม่เจอ)
// โฟลเดอร์นี้ไม่โชว์ในหน้าจัดการไฟล์ — ถ้าไม่มีแถบนี้ไฟล์ที่อัปโหลดจะหายเงียบ ๆ (เคสจริง 2026-10-08)
const POLL_MS = 30000;

export default function UnknownFilesAlert({ compact = false }) {
  const [data, setData] = useState({ count: 0, entries: [] });
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await apiGet('/api/files/unknown'));
    } catch (e) {
      if (!(e instanceof UnauthorizedError)) setData({ count: 0, entries: [] });
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const retryAll = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const res = await apiPost('/api/files/unknown/retry', { names: data.entries.map((e) => e.name) });
      const results = res.results || [];
      const ok = results.filter((r) => r.status === 'requeued').length;
      const failed = results.filter((r) => r.status !== 'requeued');
      setMsg(failed.length
        ? { ok: false, text: `ส่งเข้าใหม่ ${ok} ไฟล์ · ไม่สำเร็จ ${failed.length}: ${failed.map((f) => `${f.name} (${f.error})`).join(', ')}` }
        : { ok: true, text: `ส่ง ${ok} ไฟล์เข้า RETAILER-DATA แล้ว ระบบจะประมวลผลในรอบถัดไป (ภายใน 1 นาที)` });
      await load();
      window.dispatchEvent(new Event('ttnbcp:unknown-changed')); // ให้ป้ายตัวเลขบนแท็บอัปเดตทันที
    } catch (e) {
      setMsg({ ok: false, text: 'ส่งเข้าใหม่ไม่สำเร็จ ติดต่อเซิร์ฟเวอร์ไม่ได้' });
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  };

  if (!data.count && !msg) return null;
  if (!data.count) {
    return (
      <section className="notice info">
        <div className="notice-head">
          <p style={{ marginTop: 0 }}>{msg.text}</p>
          <button className="btn-ghost" onClick={() => setMsg(null)}>ปิด</button>
        </div>
      </section>
    );
  }

  const receivers = [...new Set(data.entries.map((e) => e.receiver).filter(Boolean))];

  if (compact) {
    return (
      <section className="notice">
        <div className="notice-head">
          <div>
            <h3><span className="tag">UNKNOW</span>{data.count} ไฟล์ค้างอยู่ ระบบไม่รู้ว่าเป็นของ TUN ไหน</h3>
            <p>ไฟล์ที่อัปโหลดแล้วไม่ไปถึง TUN จะมาค้างที่นี่ ดูรายการและส่งเข้าใหม่ได้ที่หน้าภาพรวม</p>
          </div>
          <Link className="btn btn-outline btn-sm" to="/dashboard">ดูรายการ</Link>
        </div>
      </section>
    );
  }

  return (
    <section className="notice" aria-label="ไฟล์ที่ไม่รู้ปลายทาง">
      <div className="notice-head">
        <div>
          <h3><span className="tag">UNKNOW</span>{data.count} ไฟล์ยังไม่รู้ว่าเป็นของ TUN ไหน</h3>
          <p>
            รหัสผู้รับใน UNB ไม่ตรงกับ Mailbox ของลูกค้ารายใดใน cms_customers ไฟล์ยังอยู่ครบ
            เพิ่มรหัสนี้เป็น Mailbox ของลูกค้าใน CMS ก่อน แล้วกดส่งเข้าใหม่
          </p>
        </div>
        <button className="btn btn-outline btn-sm" disabled={busy} onClick={() => setConfirm(true)}>
          <Icon name="retry" />ส่งเข้าใหม่ทั้งหมด
        </button>
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>ไฟล์</th><th>ผู้ส่ง (UNB)</th><th>ผู้รับที่หาไม่เจอ</th><th>เข้ามาเมื่อ</th></tr></thead>
          <tbody>
            {data.entries.map((e) => (
              <tr key={e.name}>
                <td><span className="file" title={e.name}>{e.name}</span></td>
                <td>{e.sender ? <span className="code">{e.sender}</span> : <span className="muted">—</span>}</td>
                <td>{e.receiver ? <span className="code" style={{ color: 'var(--warn)' }}>{e.receiver}</span> : <span className="muted">อ่าน UNB ไม่ได้</span>}</td>
                <td className="num nowrap">{fmtEpoch(e.mtime)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {msg && <div className={`msg inline-msg ${msg.ok ? 'ok' : 'err'}`}>{msg.text}</div>}

      <Modal open={confirm} title="ส่งไฟล์เข้าใหม่" onClose={() => setConfirm(false)} busy={busy}
        actions={(
          <>
            <button className="btn btn-outline" onClick={() => setConfirm(false)} disabled={busy}>ยกเลิก</button>
            <button className="btn btn-primary" onClick={retryAll} disabled={busy}>{busy ? 'กำลังส่ง…' : 'ส่งเข้าใหม่'}</button>
          </>
        )}>
        <p>ส่ง {data.count} ไฟล์จาก UNKNOW กลับเข้า RETAILER-DATA</p>
        {receivers.length > 0 && (
          <p>ถ้ายังไม่ได้เพิ่ม Mailbox {receivers.map((r) => <span key={r} className="code" style={{ marginRight: 4 }}>{r}</span>)} ใน CMS ไฟล์จะกลับมาที่ UNKNOW อีก</p>
        )}
      </Modal>
    </section>
  );
}
